import type { Movement } from '../../types';
import { PlaneLanding, PlaneTakeoff, UserCircle2, Flame, Map, AlertTriangle } from 'lucide-react';

export function TimelineView({ movements }: { movements: Movement[] }) {
    
    // Simulate time slots (06:00 to 18:00)
    const hours = Array.from({ length: 13 }, (_, i) => i + 6);

    return (
        <div className="h-full flex flex-col pt-2 pb-6 max-w-[1700px] mx-auto bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
             
             {/* Header Time Axis */}
             <div className="flex items-center border-b border-slate-200 bg-slate-50 sticky top-0 z-20">
                 {/* FBO Staff Identity Column Header */}
                 <div className="w-[320px] shrink-0 p-3 border-r border-slate-200 font-semibold text-slate-600 text-sm flex items-center justify-between">
                    <span>Operation Details</span>
                    <span className="text-xs font-medium text-slate-400">Loc / Client</span>
                 </div>
                 <div className="flex-1 flex items-center relative h-12">
                     {hours.map(h => (
                         <div key={h} className="flex-1 border-l border-slate-200 h-full flex items-end pb-1 pl-1 text-xs font-mono font-medium text-slate-400">
                             {h.toString().padStart(2, '0')}:00
                         </div>
                     ))}

                     {/* NOW line relative positioning */}
                     <div className="absolute top-0 bottom-0 left-[35%] w-0.5 bg-blue-500 z-30 pointer-events-none">
                         <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 shadow-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
                            NOW
                         </div>
                     </div>
                 </div>
             </div>

             <div className="flex-1 overflow-y-auto relative z-10 custom-scrollbar pb-8">
                 {/* Background Grid Lines */}
                 <div className="absolute inset-0 flex ml-[320px] pointer-events-none z-0">
                     {hours.map(h => (
                         <div key={h} className="flex-1 border-l border-slate-100/60 h-full"></div>
                     ))}
                 </div>

                 {movements.map((m, idx) => (
                     <TimelineRow key={m.id} movement={m} index={idx} />
                 ))}

                 {/* Crew Load Sparkline */}
                 <div className="flex items-center border-t border-slate-200 bg-white mt-12 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] sticky bottom-0 z-30">
                     <div className="w-[320px] shrink-0 p-4 border-r border-slate-200 bg-slate-50/50 flex flex-col justify-center">
                         <div className="font-bold text-slate-800 text-sm tracking-tight flex items-center gap-2">
                             <UserCircle2 size={16} className="text-slate-400" />
                             Crew Load &amp; Bottlenecks
                         </div>
                         <div className="text-xs text-slate-500 mt-1">Aggregated service staff required</div>
                     </div>
                     <div className="flex-1 flex items-end relative h-[72px] p-2 gap-1 items-end pt-2 bg-gradient-to-t from-slate-50 to-white">
                        {/* More realistic density bars */}
                        <div className="w-[8%] h-[20%] bg-slate-200 rounded-t border border-slate-300/50 relative group"></div>
                        <div className="w-[8%] h-[40%] bg-blue-300 rounded-t border border-blue-400/50"></div>
                        <div className="w-[8%] h-[60%] bg-blue-400 rounded-t border border-blue-500/50"></div>
                        <div className="w-[8%] h-[20%] bg-slate-200 rounded-t border border-slate-300/50"></div>
                        <div className="w-[30%] h-[15%] bg-slate-100 rounded-t border border-slate-200"></div>
                        {/* Peak time */}
                        <div className="w-[12%] h-[95%] bg-red-400 rounded-t border border-red-500 relative group cursor-pointer transition-colors hover:bg-red-500">
                             <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                                Max Capacity Reached
                             </div>
                        </div>
                        <div className="w-[10%] h-[50%] bg-amber-300 rounded-t border border-amber-400/50"></div>
                        <div className="w-[16%] h-[30%] bg-blue-300 rounded-t border border-blue-400/50"></div>
                     </div>
                 </div>

             </div>
        </div>
    );
}

function TimelineRow({ movement, index }: { movement: Movement, index: number }) {
    const isArrival = movement.direction === 'inbound';
    
    // Calculate simulated positions
    const offsets = [
        { left: '15%', width: '35%' },
        { left: '20%', width: '22%' },
        { left: '28%', width: '25%' },
        { left: '42%', width: '15%' },
        { left: '55%', width: '40%' }, 
        { left: '60%', width: '25%' }, 
        { left: '72%', width: '18%' },
    ];
    const pos = offsets[index] || { left: '50%', width: '10%' };

    // Render inner Gantt bar details depending on if stacking is required
    const renderGanttBar = () => {
        if (movement.isStackingRequired) {
            return (
                <div className="flex h-full w-full relative outline outline-1 outline-slate-900/10 rounded-md overflow-hidden bg-white hover:ring-2 hover:ring-amber-500 hover:z-20 transition-all cursor-pointer shadow-sm group/bar">
                     <div className="h-full w-[25%] bg-blue-100 border-r border-blue-200/60 flex items-center justify-center text-blue-600 relative">
                        <Map size={14} className="opacity-70 group-hover/bar:scale-110 transition-transform"/>
                     </div>
                     <div className="h-full w-[30%] bg-emerald-100 border-r border-emerald-200/60 flex items-center justify-center text-emerald-600 relative">
                        <Flame size={14} className="opacity-70 group-hover/bar:scale-110 transition-transform" />
                     </div>
                     <div className="h-full w-[45%] bg-amber-400 flex items-center justify-center text-amber-900 font-bold text-[10px] tracking-widest uppercase bg-[length:10px_10px] bg-[linear-gradient(45deg,rgba(0,0,0,.05)_25%,transparent_25%,transparent_50%,rgba(0,0,0,.05)_50%,rgba(0,0,0,.05)_75%,transparent_75%,transparent)]">
                        <AlertTriangle size={14} className="mr-1 opacity-80" /> <span className="hidden group-hover/bar:inline-block ml-1">Stacking Req</span>
                     </div>
                </div>
            );
        }
        
        return (
             <div className="flex h-full w-full relative outline outline-1 outline-slate-900/10 rounded-md overflow-hidden bg-white hover:ring-2 hover:ring-blue-400 hover:z-20 transition-all cursor-pointer shadow-sm group/bar">
                 <div className="h-full w-[40%] bg-blue-100 border-r border-blue-200/60 flex items-center justify-center text-blue-600 relative">
                     <Map size={14} className="opacity-70 group-hover/bar:scale-110 transition-transform"/>
                 </div>
                 <div className="h-full w-[60%] bg-emerald-100 flex items-center justify-center text-emerald-600 relative">
                     <Flame size={14} className="opacity-70 group-hover/bar:scale-110 transition-transform" />
                     <div className="absolute right-2 opacity-0 group-hover/bar:opacity-100 transition-opacity text-[9px] font-bold">500 GAL</div>
                 </div>
             </div>
        );
    };

    return (
        <div className="flex items-stretch border-b border-slate-100 hover:bg-slate-50/80 group transition-colors">
            
            {/* Identity Column (FBO Detail Dense) */}
            <div className="w-[320px] shrink-0 p-3 pr-4 border-r border-slate-200 flex flex-col justify-center bg-white group-hover:bg-slate-50/50 relative z-20">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <div className={`p-1 mt-0.5 rounded ${isArrival ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
                            {isArrival ? <PlaneLanding size={14} /> : <PlaneTakeoff size={14} />}
                        </div>
                        <div>
                            <div className="font-bold text-slate-800 text-sm tracking-tight flex items-baseline gap-1.5">
                                {movement.aircraft.tailNumber}
                                <span className="text-[10px] text-slate-500 font-semibold">{movement.aircraft.type}</span>
                            </div>
                            <div className="text-[11px] font-medium text-slate-500 truncate max-w-[150px] leading-tight mt-0.5">
                                {movement.clientName}
                            </div>
                        </div>
                    </div>
                    
                    <div className="text-right flex flex-col items-end justify-start">
                        <span className="font-mono text-xs font-bold text-slate-700">{movement.scheduledTime}</span>
                        <span className="text-[10px] font-bold text-slate-800 bg-slate-100 px-1 py-0.5 rounded mt-0.5 truncate max-w-[80px]">
                            {movement.assignedSpace}
                        </span>
                    </div>
                </div>
                
                {movement.isStackingRequired && (
                   <div className="mt-1.5 ml-8 text-[10px] font-medium text-amber-700 flex items-center gap-1 bg-amber-50 px-1.5 py-0.5 rounded w-max border border-amber-200">
                       <AlertTriangle size={10} /> Requires Hangar Pull
                   </div>
                )}
            </div>

            {/* Gantt Bar Area */}
            <div className="flex-1 relative min-h-[56px] z-10 flex items-center my-1 group/row">
                
                {/* Background alternating stripe for readability on hover */}
                <div className="absolute inset-0 bg-transparent group-hover/row:bg-slate-900/[0.02] pointer-events-none z-0"></div>

                {/* Idle Background line (the "stay duration" mockup) */}
                <div className="absolute left-0 h-px bg-slate-300 border-b border-slate-300 border-dashed top-1/2 -translate-y-1/2 z-0" style={{ right: `calc(100% - ${pos.left})` }}></div>
                
                {/* The Bar Block */}
                <div 
                  className="absolute h-7 z-10"
                  style={{ left: pos.left, width: pos.width }}
                >
                   {renderGanttBar()}
                   
                   {/* Tooltip mockup indicating detailed timing on hover */}
                   <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 group-hover/row:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 flex items-center gap-1.5">
                       <span>{movement.scheduledTime} &ndash; 12:45</span>
                       <span className="opacity-50 text-slate-300">|</span>
                       <span>4 Services</span>
                   </div>
                </div>

                {/* Event point visually mapped near the bar start */}
                <div className="absolute w-2.5 h-2.5 rounded-full bg-slate-800 border-2 border-white shadow-sm z-20 top-1/2 -translate-y-1/2" style={{ left: `calc(${pos.left} - 4px)` }}></div>
            </div>
        </div>
    )
}
